import type {CSSProperties, KeyboardEvent, ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {ClickableDiv} from '@Components/clickable-div';
import {AiChatChristmasGiftUnlock} from '@AiChat/components/ai-chat-christmas-gift-unlock/ai-chat-christmas-gift-unlock';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {AiChatChristmasAssetImage} from '@AiChat/components/ai-chat-christmas-asset-image/ai-chat-christmas-asset-image';
import {AiChatChristmasAsset} from '@AiChat/lib/ai-chat-season-library.types';
import {getAiChatMotionDurationMs} from '@AiChat/lib/ai-chat-motion.library';
import type {CssVariableStyle} from '@AiChat/lib/ai-chat-style.types';
import {KEYBOARD_KEY} from '@Utils/dom.util';
import type {AiChatChristmasGiftBurst, AiChatChristmasGiftGlint, AiChatChristmasGiftsAccessibilityProps, AiChatChristmasGiftSpark} from './ai-chat-christmas-gifts.types';
import {AI_CHAT_CHRISTMAS_GIFT_BURSTS, AI_CHAT_CHRISTMAS_GIFT_GLINTS, AI_CHAT_CHRISTMAS_GIFT_SPARKS, GIFT_UNLOCK_DELAY_MS} from './ai-chat-christmas-gifts.types';
import styles from './ai-chat-christmas-gifts.module.scss';

export function AiChatChristmasGifts(): ReactElement {
  const {state} = useAiChat();
  const [hopCount, setHopCount] = useState<number>(0);
  const [isHopping, setIsHopping] = useState<boolean>(false);
  const [isUnlockScheduled, setIsUnlockScheduled] = useState<boolean>(false);
  const [isUnlockOpen, setIsUnlockOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!isUnlockScheduled) {
      return undefined;
    }

    const unlockTimer = window.setTimeout(() => {
      setIsUnlockScheduled(false);
      setIsUnlockOpen(true);
    }, getAiChatMotionDurationMs(GIFT_UNLOCK_DELAY_MS));

    return (): void => {
      window.clearTimeout(unlockTimer);
    };
  }, [isUnlockScheduled]);

  const onGiftsClick = (): void => {
    setHopCount(hopCount + 1);
    setIsHopping(true);

    if (canUnwrapGift()) {
      setIsUnlockScheduled(true);
    }
  };

  const canUnwrapGift = (): boolean => {
    return state.christmasGiftOffer !== null;
  };

  const onGiftsKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== KEYBOARD_KEY.SPACE || event.repeat) {
      return;
    }

    event.preventDefault();
    onGiftsClick();
  };

  const onGiftsArtAnimationEnd = (): void => {
    setIsHopping(false);
  };

  const onGiftUnlockClose = (): void => {
    setIsUnlockOpen(false);
  };

  const getGiftsClassName = (): string => {
    if (isHopping) {
      return `${styles.gifts} ${styles.isHopping}`;
    }

    return styles.gifts;
  };

  const getGlint = (glint: AiChatChristmasGiftGlint, index: number): ReactElement => {
    return <span key={index} className={styles.glint} style={getGlintStyle(glint)} />;
  };

  const getGlintStyle = (glint: AiChatChristmasGiftGlint): CSSProperties => {
    return {
      animationDelay: `${glint.delaySeconds}s`,
      left: `${glint.leftPercent}%`,
      top: `${glint.topPercent}%`,
    };
  };

  const getBurst = (burst: AiChatChristmasGiftBurst, index: number): ReactElement => {
    return <span key={index} className={styles.burst} style={{left: `${burst.leftPercent}%`, top: `${burst.topPercent}%`}} />;
  };

  const getSpark = (spark: AiChatChristmasGiftSpark, index: number): ReactElement => {
    return <span key={index} className={styles.spark} style={getSparkStyle(spark)} />;
  };

  const getSparkStyle = (spark: AiChatChristmasGiftSpark): CssVariableStyle => {
    return {
      '--spark-drift-x': `${spark.driftXPx}px`,
      '--spark-rise-y': `${-spark.riseYPx}px`,
      '--spark-size': `${spark.sizePx}px`,
    };
  };

  const getGiftUnlock = (): ReactElement | null => {
    if (!isUnlockOpen) {
      return null;
    }

    return <AiChatChristmasGiftUnlock onClose={onGiftUnlockClose} />;
  };

  const getGifts = (): ReactElement => {
    return (
      <ClickableDiv className={getGiftsClassName()} enableFocusOutline={false} onClick={onGiftsClick} {...getGiftsAccessibilityProps()}>
        {getGiftsBody()}
      </ClickableDiv>
    );
  };

  const getGiftsAccessibilityProps = (): AiChatChristmasGiftsAccessibilityProps => {
    if (canUnwrapGift()) {
      return {
        tabIndex: 0,
        'aria-label': window.i18next.t('pmwjs_ai_chat_christmas_gift_unwrap'),
        onKeyDown: onGiftsKeyDown,
      };
    }

    return {
      tabIndex: -1,
      'aria-hidden': true,
    };
  };

  const getGiftsBody = (): ReactElement => {
    return (
      <span key={hopCount} className={styles.giftsBody}>
        <AiChatChristmasAssetImage asset={AiChatChristmasAsset.GIFTS} className={styles.giftsArt} onAnimationEnd={onGiftsArtAnimationEnd} />
        {AI_CHAT_CHRISTMAS_GIFT_GLINTS.map(getGlint)}
        {AI_CHAT_CHRISTMAS_GIFT_BURSTS.map(getBurst)}
        {AI_CHAT_CHRISTMAS_GIFT_SPARKS.map(getSpark)}
      </span>
    );
  };

  return (
    <>
      {getGifts()}
      {getGiftUnlock()}
    </>
  );
}
